import React from 'react';
import { motion } from 'framer-motion';
import {
  Award,
  Camera,
  MapPin,
  Users,
  Zap,
  ShieldCheck,
  ArrowRight,
  Star,
} from 'lucide-react';
import { trackWhatsAppClick } from '../utils/analytics';

/**
 * About / "Por Que Coutin" — conversion-focused authority section.
 * Replaces vanity timeline with: founder photo + sharp positioning +
 * benefit-driven differentials + social proof stats + CTA.
 */
const About = () => {
  const differentials = [
    {
      icon: MapPin,
      title: 'Equipe Presente em Foz do Iguaçu',
      desc: 'Atendemos pessoalmente — não somos uma agência remota que some depois do contrato.',
    },
    {
      icon: Camera,
      title: 'Equipamento Profissional Próprio',
      desc: 'Câmeras Sony, drones DJI Avata FPV e gimbals. Tudo nosso — não terceirizamos a qualidade da entrega.',
    },
    {
      icon: Users,
      title: 'Equipe Full-Service Sob o Mesmo Teto',
      desc: 'Filmagem, edição, design, sites e tráfego pago. Você não precisa contratar 5 freelancers.',
    },
    {
      icon: Zap,
      title: 'Resposta em até 2h Úteis',
      desc: 'WhatsApp ativo. Briefing rápido. Cronograma claro. Sem enrolação.',
    },
    {
      icon: Award,
      title: '10 Anos de Estrada',
      desc: 'Desde 2015 entregamos resultado para empresas da região da Tríplice Fronteira.',
    },
    {
      icon: ShieldCheck,
      title: 'Avaliação Máxima no Google',
      desc: '5,0 ★ verificadas. Toda recomendação é de quem realmente trabalhou conosco.',
    },
  ];

  const handleCTA = () => {
    trackWhatsAppClick('about-section', 'Conhecer a Coutin');
    window.open(
      `https://wa.me/5545988068508?text=${encodeURIComponent(
        'Olá! Vim pelo site e quero conhecer melhor o trabalho da Coutin.'
      )}`,
      '_blank'
    );
  };

  return (
    <section
      id="sobre"
      className="relative py-24 md:py-32 bg-[#0A0A0A] overflow-hidden"
      data-testid="about-section"
    >
      {/* Ambient backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[40vw] h-[40vw] bg-[#4ECDC4]/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[40vw] h-[40vw] bg-[#4ECDC4]/8 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Top Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 mb-4">
            <div className="w-8 h-[1px] bg-[#4ECDC4]" />
            <span className="text-xs tracking-[0.3em] uppercase font-bold text-[#4ECDC4]">
              Quem Está por Trás da Sua Marca
            </span>
            <div className="w-8 h-[1px] bg-[#4ECDC4]" />
          </div>
          <h2 className="text-3xl md:text-5xl tracking-tight font-bold text-[#F5F5F0] mb-4 max-w-4xl mx-auto">
            Uma operação de{' '}
            <span className="text-[#4ECDC4]">produção, estratégia e execução</span>
          </h2>
          <p className="text-base md:text-lg text-[#A1A1AA] max-w-2xl mx-auto font-light leading-relaxed">
            Coutin Agência é uma empresa híbrida de audiovisual e marketing digital, sediada em
            Foz do Iguaçu, com capacidade operacional em toda a Tríplice Fronteira.
          </p>
        </motion.div>

        {/* Founder photo + stats */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-20 items-center max-w-6xl mx-auto">
          {/* Photo */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-[#4ECDC4]/30 shadow-[0_30px_80px_-20px_rgba(78,205,196,0.35)]">
              <img
                src="/images/founder.jpg"
                alt="Coutin Agência em ação — captação profissional"
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid="founder-photo"
              />
              {/* Cinematic gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-70" />
              {/* Frame caption */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#4ECDC4] font-bold mb-1">
                  Desde 2015 · Foz do Iguaçu, PR
                </p>
                <p className="text-[#F5F5F0] text-lg md:text-xl font-light italic leading-tight">
                  "Cada frame que captamos carrega o futuro da sua marca."
                </p>
              </div>
            </div>

            {/* Floating Google rating badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="absolute -bottom-6 -right-4 md:-right-6 bg-[#0A0A0A] border border-[#4ECDC4]/40 rounded-2xl px-5 py-4 shadow-2xl backdrop-blur-md"
              data-testid="about-google-badge"
            >
              <div className="flex items-center gap-3">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 text-[#4ECDC4] fill-[#4ECDC4]"
                    />
                  ))}
                </div>
                <div>
                  <div className="text-[#F5F5F0] font-bold text-lg leading-none">
                    5,0
                  </div>
                  <div className="text-[#A1A1AA] text-[10px] uppercase tracking-wider">
                    Google
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Story + stats */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-7"
          >
            <span className="inline-block text-[10px] uppercase tracking-[0.4em] text-[#4ECDC4] font-bold mb-4">
              A Coutin em Números
            </span>
            <h3 className="text-2xl md:text-4xl font-bold text-[#F5F5F0] leading-tight mb-6">
              Uma agência local, com{' '}
              <span className="text-[#4ECDC4]">padrão de cinema</span>.
            </h3>
            <p className="text-base text-[#A1A1AA] mb-10 leading-relaxed font-light">
              Desde 2015, unimos <strong className="text-[#F5F5F0]">produção audiovisual</strong>,{' '}
              <strong className="text-[#F5F5F0]">marketing digital</strong> e{' '}
              <strong className="text-[#F5F5F0]">execução estratégica</strong> em Foz do Iguaçu e
              na Tríplice Fronteira. Uma década de estrada nos preparou para atender desde
              produções convencionais até projetos que exigem discrição, velocidade e
              responsabilidade.
            </p>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-4 md:gap-6">
              {[
                { num: '10 anos', label: 'De estrada' },
                { num: 'Tríplice', label: 'Fronteira' },
                { num: '5,0★', label: 'Google verificado' },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                  className="border border-white/10 hover:border-[#4ECDC4]/40 rounded-xl p-4 md:p-5 bg-gradient-to-br from-[#161616] to-[#0E0E0E] transition-colors duration-300"
                  data-testid={`about-stat-${i}`}
                >
                  <div className="text-3xl md:text-4xl font-black text-[#4ECDC4] mb-1 leading-none">
                    {stat.num}
                  </div>
                  <div className="text-[10px] md:text-xs text-[#A1A1AA] uppercase tracking-wider font-medium">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Differentials grid */}
        <div className="max-w-6xl mx-auto">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-2xl md:text-3xl font-bold text-[#F5F5F0] mb-10 text-center"
          >
            6 motivos para fechar com a{' '}
            <span className="text-[#4ECDC4]">Coutin</span>
          </motion.h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {differentials.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="group relative border border-white/10 hover:border-[#4ECDC4]/40 rounded-2xl p-6 bg-gradient-to-br from-[#161616] to-[#0E0E0E] hover:from-[#1A1A1A] hover:to-[#0E0E0E] transition-all duration-400"
                  data-testid={`about-differential-${i}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-[#4ECDC4]/15 border border-[#4ECDC4]/30 flex items-center justify-center mb-4 group-hover:scale-110 group-hover:bg-[#4ECDC4]/25 transition-all duration-300">
                    <Icon className="w-5 h-5 text-[#4ECDC4]" strokeWidth={2} />
                  </div>
                  <h4 className="text-base md:text-lg font-bold text-[#F5F5F0] mb-2 leading-tight">
                    {item.title}
                  </h4>
                  <p className="text-sm text-[#A1A1AA] font-light leading-relaxed">
                    {item.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-16"
        >
          <p className="text-[#A1A1AA] mb-5 text-base md:text-lg font-light">
            Quer fazer um café (presencial ou virtual) e conhecer o trabalho?
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleCTA}
            className="inline-flex items-center space-x-2 bg-[#4ECDC4] hover:bg-[#F5F5F0] text-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 rounded-lg transition-all duration-300 shadow-xl shadow-[#4ECDC4]/20"
            data-testid="about-cta"
          >
            <span>Vamos Conversar</span>
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
